import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useOutlet } from '../lib/OutletContext';
import {
  getRecipes,
  getSalesCaptureUnmappedImpact,
  getSalesCaptureSyncRuns,
  getItemMappings,
  saveItemMapping,
  getMappingSuggestions,
  publishAndMap,
} from '../lib/api';
import {
  Link2, Link2Off, Search, RefreshCw, ArrowLeft, Check,
  AlertCircle, TrendingUp, BookOpen, Info, Sparkles, Upload, Zap,
} from 'lucide-react';
import { toast } from 'sonner';
import { cn } from '../lib/utils';
import logger from '../lib/logger';
import BulkMappingImportDialog from '../components/BulkMappingImportDialog';
import DepletionReadinessPanel from '../components/DepletionReadinessPanel';
import ModePromotionPanel from '../components/ModePromotionPanel';

// ─────────────────────────────────────────────────────────────────────────────
// Sales Item Mapping
// ─────────────────────────────────────────────────────────────────────────────
// This page is the canonical UI for the Sales Capture resolver precedence #2
// (sales_item_mappings). A mapping created here goes directly into the same
// collection that Phase 2 coverage is computed against. Legacy POS mapping
// (recipes.pos_mapping.pos_item_id) is intentionally NOT used here.
//
// Data sources:
//   • Unmapped items  → GET /api/sales-capture/unmapped-impact   (Sales Capture)
//   • Coverage stats  → GET /api/sales-capture/sync-runs?limit=1 (latest run)
//   • Existing maps   → GET /api/inventory/sales/item-mappings
//   • Recipes         → GET /api/inventory/recipes?status=PUBLISHED&type=MENU_ITEM
//
// Writes:
//   • POST /api/inventory/sales/item-mappings { item_name, recipe_id, recipe_name }
//     → db.sales_item_mappings  (Sales Capture resolver reads this)
// ─────────────────────────────────────────────────────────────────────────────

const COVERAGE_THRESHOLD = 85;

const MenuMapping = () => {
  const navigate = useNavigate();
  const { selectedOutlet } = useOutlet();

  // Data
  const [unmappedItems, setUnmappedItems] = useState([]);      // from sales-capture
  const [recipes, setRecipes] = useState([]);                  // ALL non-archived MENU_ITEM recipes (PUBLISHED + DRAFT)
  const [existingMappings, setExistingMappings] = useState([]); // item_name_lower → recipe
  const [lastRun, setLastRun] = useState(null);                // latest sync run for stats
  const [suggestionsByItem, setSuggestionsByItem] = useState({}); // item_name → [candidates]

  // Selection
  const [selectedItem, setSelectedItem] = useState(null);      // unmapped sales item
  const [selectedRecipe, setSelectedRecipe] = useState(null);

  // Filter
  const [itemSearch, setItemSearch] = useState('');
  const [recipeSearch, setRecipeSearch] = useState('');

  // UI
  const [loading, setLoading] = useState(true);
  const [mapping, setMapping] = useState(false);
  const [sourceWarning, setSourceWarning] = useState(null);
  const [suggestionsLoading, setSuggestionsLoading] = useState(false);
  const [bulkImportOpen, setBulkImportOpen] = useState(false);

  const loadData = useCallback(async () => {
    if (!selectedOutlet) {
      setLoading(false);
      return;
    }

    setLoading(true);
    setSourceWarning(null);

    try {
      // Recipes — ALL non-archived MENU_ITEM recipes (Phase 2 May 7 2026: include
      // DRAFT so the UI can offer "Publish & Map" inline). The Sales Capture
      // resolver still strictly requires status='PUBLISHED' for depletion.
      const recipesData = await getRecipes({
        outlet_id: selectedOutlet,
        type: 'MENU_ITEM',
      });
      const recipesList = (recipesData.recipes || []).filter(
        (r) => (r.status || 'DRAFT').toUpperCase() !== 'ARCHIVED'
      );
      setRecipes(recipesList);

      // Existing item→recipe maps (org-scoped)
      const mappingsData = await getItemMappings();
      setExistingMappings(mappingsData.mappings || []);

      // Unmapped Sales Capture items for this outlet (ordered by qty impact)
      let impactItems = [];
      try {
        const impact = await getSalesCaptureUnmappedImpact(selectedOutlet, 'quantity', 200);
        impactItems = impact.items || [];
        setUnmappedItems(impactItems);
        if (!impactItems.length) {
          setSourceWarning(
            'No unmapped Sales Capture items yet for this outlet. Run a shadow/monitor sync to populate the unmapped set, or all items are already mapped.'
          );
        }
      } catch (e) {
        logger.warn('[MenuMapping] unmapped-impact fetch failed:', e);
        setUnmappedItems([]);
        setSourceWarning('Sales Capture unavailable. Verify SALES_CAPTURE_BASE_URL and service token.');
      }

      // Latest sync run — for coverage stats
      try {
        const runs = await getSalesCaptureSyncRuns(selectedOutlet, 1);
        setLastRun(runs?.runs?.[0] || null);
      } catch (e) {
        logger.warn('[MenuMapping] sync-runs fetch failed:', e);
        setLastRun(null);
      }

      // Auto-suggest mappings for the top unmapped items.
      // Bulk-fetch ONCE per loadData; never auto-applied — operator confirms.
      if (impactItems.length > 0) {
        setSuggestionsLoading(true);
        try {
          const itemNames = impactItems.slice(0, 200).map((i) => i.item_name).filter(Boolean);
          const sugRes = await getMappingSuggestions(itemNames, 3);
          const map = {};
          (sugRes.suggestions || []).forEach((s) => {
            map[(s.item_name || '').toLowerCase()] = s.candidates || [];
          });
          setSuggestionsByItem(map);
        } catch (e) {
          logger.warn('[MenuMapping] suggestions fetch failed:', e);
          setSuggestionsByItem({});
        } finally {
          setSuggestionsLoading(false);
        }
      } else {
        setSuggestionsByItem({});
      }
    } catch (error) {
      toast.error('Failed to load data');
      logger.error('[MenuMapping] Error:', error);
    } finally {
      setLoading(false);
    }
  }, [selectedOutlet]);

  useEffect(() => { loadData(); }, [loadData]);

  // Modal state for confirmation flows (Trust Polish — May 7 2026)
  // - low_confidence: showing review modal for LOW-tier suggestion
  // - multi_map: recipe already mapped to other items; ask intentional_multi_map
  // - publishability: recipe failed structural validation; show diagnostics
  const [confirmModal, setConfirmModal] = useState(null);

  const _toastError = (error) => {
    const detail = error?.response?.data?.detail;
    if (detail?.error === 'ALREADY_MAPPED_DIFFERENT_RECIPE') {
      toast.error(`Already mapped to "${detail.current_recipe_name}". Delete that mapping first.`);
      return true;
    }
    if (detail?.error === 'RECIPE_ALREADY_MAPPED') {
      // Surface multi-map confirmation modal — operator must explicitly opt-in.
      setConfirmModal({
        kind: 'multi_map',
        intent: confirmModal?.intent || null,
        other_mappings: detail.other_mappings || [],
      });
      return true;
    }
    if (detail?.error === 'RECIPE_NOT_PUBLISHABLE') {
      setConfirmModal({
        kind: 'publishability',
        recipe_name: detail.recipe_name || '?',
        diagnostics: detail.diagnostics || [],
      });
      return true;
    }
    return false;
  };

  // Underlying mapping action — extracted so both manual flow and one-click
  // suggestion flow can share retry-with-multi-map.
  const _applyMapping = async ({ item, recipe, intentional_multi_map = false }) => {
    return publishAndMap({
      item_name: item.item_name,
      recipe_id: recipe.recipe_id,
      item_id: item.item_id || item.sample_item_id || null,
      item_category: item.item_category || item.category || null,
      outlet_id: selectedOutlet,
      intentional_multi_map,
    });
  };

  const handleMap = async ({ intentional_multi_map = false } = {}) => {
    if (!selectedItem || !selectedRecipe) {
      toast.error('Select both a sales item and a recipe');
      return;
    }
    setMapping(true);
    try {
      const res = await _applyMapping({
        item: selectedItem, recipe: selectedRecipe, intentional_multi_map,
      });
      toast.success(
        res.recipe_was_published_now
          ? `Published "${selectedRecipe.recipe_name}" and mapped to "${selectedItem.item_name}"`
          : `Mapped "${selectedItem.item_name}" → "${selectedRecipe.recipe_name}"`
      );
      setSelectedItem(null);
      setSelectedRecipe(null);
      setConfirmModal(null);
      loadData();
    } catch (error) {
      // Stash intent so the modal's "Confirm" can retry with multi-map flag set.
      if (!_toastError(error)) {
        toast.error('Failed to create mapping');
      } else if (error?.response?.data?.detail?.error === 'RECIPE_ALREADY_MAPPED') {
        setConfirmModal((cur) => ({ ...(cur || {}), intent: { kind: 'manual' } }));
      }
      logger.error('[MenuMapping] Error:', error);
    } finally {
      setMapping(false);
    }
  };

  // Suggestion apply (one-click). LOW tier triggers a review modal first.
  const handleApplySuggestion = async (item, candidate, opts = {}) => {
    if (!candidate) return;
    if ((candidate.tier || 'LOW') === 'LOW' && !opts.confirmed) {
      setConfirmModal({
        kind: 'low_confidence',
        item, candidate,
      });
      return;
    }
    setMapping(true);
    try {
      const res = await _applyMapping({
        item, recipe: candidate, intentional_multi_map: !!opts.intentional_multi_map,
      });
      toast.success(
        res.recipe_was_published_now
          ? `Published "${candidate.recipe_name}" and mapped to "${item.item_name}"`
          : `Mapped "${item.item_name}" → "${candidate.recipe_name}"`
      );
      setConfirmModal(null);
      loadData();
    } catch (error) {
      if (!_toastError(error)) {
        toast.error('Failed to apply suggestion');
      } else if (error?.response?.data?.detail?.error === 'RECIPE_ALREADY_MAPPED') {
        setConfirmModal((cur) => ({ ...(cur || {}), intent: { kind: 'suggestion', item, candidate } }));
      }
      logger.error('[MenuMapping] Apply suggestion error:', error);
    } finally {
      setMapping(false);
    }
  };

  // User confirmed an intent from the multi_map modal — retry with the flag set.
  const handleConfirmMultiMap = () => {
    const intent = confirmModal?.intent;
    if (!intent) { setConfirmModal(null); return; }
    if (intent.kind === 'manual') {
      handleMap({ intentional_multi_map: true });
    } else if (intent.kind === 'suggestion') {
      handleApplySuggestion(intent.item, intent.candidate, {
        confirmed: true, intentional_multi_map: true,
      });
    }
  };

  // ── Derived ───────────────────────────────────────────────────────────────
  const mappedLowerSet = new Set(
    existingMappings.map((m) => (m.item_name_lower || m.item_name || '').toLowerCase())
  );

  const filteredItems = unmappedItems.filter((it) => {
    const name = (it.item_name || '').toLowerCase();
    if (mappedLowerSet.has(name)) return false; // already mapped locally → hide
    return name.includes(itemSearch.toLowerCase());
  });

  const filteredRecipes = recipes.filter((r) =>
    (r.recipe_name || '').toLowerCase().includes(recipeSearch.toLowerCase())
  );

  // Coverage stats from latest run (source of Phase 2 truth)
  const coveragePct = lastRun?.mapping_coverage ?? null;
  const mappedRows = lastRun?.mapped_rows ?? 0;
  const validRows = lastRun?.valid_rows ?? 0;
  const unresolvedRows = lastRun?.unresolved_rows ?? 0;
  const coverageReady = coveragePct !== null && coveragePct >= COVERAGE_THRESHOLD;

  const getRecipeMappedName = (recipe) => {
    const hit = existingMappings.find((m) => m.recipe_id === recipe.recipe_id);
    return hit?.item_name || null;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64" data-testid="loading-spinner">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-emerald-500"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="menu-mapping-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <button
            onClick={() => navigate('/recipes')}
            className="p-2 rounded-md hover:bg-[#27272a] transition-colors"
            data-testid="back-button"
          >
            <ArrowLeft className="w-5 h-5" />
          </button>
          <div>
            <h1 className="text-3xl font-black tracking-tight" data-testid="menu-mapping-title">
              Sales Item Mapping
            </h1>
            <p className="text-zinc-400">Map unmapped Sales Capture items to published recipes</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={loadData}
            className="flex items-center gap-2 px-4 py-2 bg-[#27272a] hover:bg-[#3f3f46] rounded-md transition-colors text-sm font-medium"
            data-testid="refresh-button"
          >
            <RefreshCw className="w-4 h-4" />
            Refresh
          </button>
          <button
            onClick={() => setBulkImportOpen(true)}
            className="flex items-center gap-2 px-4 py-2 bg-emerald-500/10 hover:bg-emerald-500/20 text-emerald-400 border border-emerald-500/30 rounded-md transition-colors text-sm font-medium"
            data-testid="bulk-import-button"
          >
            <Upload className="w-4 h-4" />
            Bulk Import
          </button>
        </div>
      </div>

      {/* Doctrine banner */}
      <div className="flex items-start gap-3 p-3 bg-[#18181b] border border-[#27272a] rounded-lg">
        <Info className="w-4 h-4 text-zinc-400 flex-shrink-0 mt-0.5" />
        <p className="text-xs text-zinc-400 leading-relaxed">
          Mappings created here write to <code className="text-zinc-300">sales_item_mappings</code> — the same
          collection the Sales Capture resolver reads at precedence step 2. Phase 2 activation requires
          <span className="text-amber-400 font-semibold"> ≥{COVERAGE_THRESHOLD}% quantity-weighted coverage</span> on the latest sync run
          — mapping a few high-volume items moves the needle far more than a long tail of low-volume ones.
        </p>
      </div>

      {/* Depletion Readiness — multi-axis trust score (Slice 2 May 7 2026) */}
      <DepletionReadinessPanel outletId={selectedOutlet} refreshKey={existingMappings.length} />

      {/* Mode Promotion — staged ladder + audited gating (Slice 3 May 7 2026) */}
      <ModePromotionPanel outletId={selectedOutlet} refreshKey={existingMappings.length} />

      {/* Stats — from latest Sales Capture sync run */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="bg-[#18181b] border border-[#27272a] rounded-lg p-4" data-testid="stat-items-seen">
          <p className="text-xs text-zinc-400 uppercase tracking-wider mb-1">Sales Rows Seen</p>
          <p className="text-2xl font-mono font-bold">{validRows}</p>
          <p className="text-xs text-zinc-500 mt-1">last sync run</p>
        </div>
        <div className="bg-[#18181b] border border-[#27272a] rounded-lg p-4" data-testid="stat-mapped">
          <p className="text-xs text-zinc-400 uppercase tracking-wider mb-1">Mapped</p>
          <p className="text-2xl font-mono font-bold text-emerald-500">{mappedRows}</p>
          <p className="text-xs text-zinc-500 mt-1">rows resolved to recipes</p>
        </div>
        <div className="bg-[#18181b] border border-[#27272a] rounded-lg p-4" data-testid="stat-unmapped">
          <p className="text-xs text-zinc-400 uppercase tracking-wider mb-1">Unmapped</p>
          <p className="text-2xl font-mono font-bold text-amber-500">{unresolvedRows}</p>
          <p className="text-xs text-zinc-500 mt-1">rows queued as unresolved</p>
        </div>
        <div className="bg-[#18181b] border border-[#27272a] rounded-lg p-4" data-testid="stat-coverage">
          <p className="text-xs text-zinc-400 uppercase tracking-wider mb-1">Coverage</p>
          <p className={cn(
            'text-2xl font-mono font-bold',
            coveragePct === null ? 'text-zinc-500' :
              coverageReady ? 'text-emerald-500' : 'text-amber-500'
          )}>
            {coveragePct === null ? '—' : `${coveragePct.toFixed(1)}%`}
          </p>
          <p className="text-xs text-zinc-500 mt-1">threshold {COVERAGE_THRESHOLD}%</p>
        </div>
      </div>

      {/* Source warning */}
      {sourceWarning && (
        <div className="flex items-start gap-4 p-4 bg-amber-500/10 border border-amber-500/20 rounded-lg" data-testid="source-warning">
          <AlertCircle className="w-6 h-6 text-amber-500 flex-shrink-0 mt-0.5" />
          <div>
            <h3 className="font-semibold text-amber-500 mb-1">No unmapped items to show</h3>
            <p className="text-sm text-amber-100">{sourceWarning}</p>
          </div>
        </div>
      )}

      {/* Mapping interface */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Unmapped Sales Items */}
        <div className="bg-[#18181b] border border-[#27272a] rounded-lg">
          <div className="p-4 border-b border-[#27272a]">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2">
                <TrendingUp className="w-5 h-5 text-blue-400" />
                <h2 className="text-lg font-bold">Unmapped Sales Items</h2>
                <span className="text-xs text-zinc-500">(by qty impact)</span>
              </div>
            </div>
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-400" />
              <input
                type="text"
                placeholder="Search sales items..."
                value={itemSearch}
                onChange={(e) => setItemSearch(e.target.value)}
                className="w-full pl-10 pr-4 py-2 bg-[#09090b] border border-[#3f3f46] rounded-md focus:border-emerald-500 outline-none text-sm"
                data-testid="sales-item-search-input"
              />
            </div>
          </div>
          <div className="max-h-96 overflow-y-auto">
            {filteredItems.length === 0 ? (
              <div className="p-8 text-center text-zinc-500" data-testid="no-unmapped-items">
                <Link2Off className="w-12 h-12 mx-auto mb-3 opacity-50" />
                <p>{sourceWarning ? 'No unmapped items' : 'All visible items mapped'}</p>
              </div>
            ) : (
              <div className="divide-y divide-[#27272a]">
                {filteredItems.map((item) => {
                  const isSelected = selectedItem?.item_name === item.item_name;
                  const candidates = suggestionsByItem[(item.item_name || '').toLowerCase()] || [];
                  const top = candidates[0] || null;
                  const tier = top?.tier || 'LOW';
                  const tierChipClass =
                    tier === 'HIGH'   ? 'bg-emerald-500/10 text-emerald-400 border-emerald-500/30' :
                    tier === 'MEDIUM' ? 'bg-amber-500/10 text-amber-300 border-amber-500/30' :
                                        'bg-zinc-500/10 text-zinc-400 border-zinc-500/30';
                  const isDraftSuggestion = top && (top.status || '').toUpperCase() === 'DRAFT';
                  const isLowTier = tier === 'LOW';
                  const gain = item.potential_coverage_gain_pct;
                  return (
                    <div
                      key={item.item_name}
                      className={cn(
                        'border-l-2 transition-colors',
                        isSelected ? 'bg-emerald-500/10 border-emerald-500' : 'border-transparent hover:bg-[#09090b]',
                      )}
                      data-testid="sales-item"
                    >
                      <button
                        onClick={() => setSelectedItem(isSelected ? null : item)}
                        className="w-full p-4 text-left"
                      >
                        <div className="flex items-center justify-between gap-3">
                          <div className="min-w-0 flex-1">
                            <div className="flex items-center gap-2 flex-wrap">
                              <p className="font-medium truncate">{item.item_name}</p>
                              {gain !== undefined && gain !== null && gain > 0 && (
                                <span
                                  className="inline-flex px-1.5 py-0.5 rounded text-[10px] font-bold border bg-blue-500/10 text-blue-300 border-blue-500/30"
                                  data-testid="coverage-gain-chip"
                                  title="Mapping this item would add this much to weighted coverage"
                                >
                                  +{gain.toFixed(1)}% coverage
                                </span>
                              )}
                            </div>
                            <p className="text-xs text-zinc-500 mt-0.5">
                              qty sold: <span className="font-mono text-zinc-400">{item.total_qty_sold}</span>
                              {' · '}
                              occurrences: <span className="font-mono text-zinc-400">{item.occurrence_count}</span>
                              {item.sample_item_id && (
                                <> · id: <span className="font-mono text-zinc-600">{item.sample_item_id}</span></>
                              )}
                            </p>
                          </div>
                          {isSelected && (
                            <div className="w-6 h-6 rounded-full bg-emerald-500 flex items-center justify-center flex-shrink-0">
                              <Check className="w-4 h-4 text-white" />
                            </div>
                          )}
                        </div>
                      </button>
                      {/* Auto-suggestion strip — operator confirms (one-click). LOW tier gates behind review modal. */}
                      {top && (
                        <div className="px-4 pb-3 -mt-1 flex items-center gap-2 flex-wrap" data-testid={`suggestion-row-${item.item_name}`}>
                          <Sparkles className="w-3.5 h-3.5 text-violet-400 flex-shrink-0" />
                          <span className="text-[11px] text-zinc-500">Suggested:</span>
                          <span className="text-xs text-zinc-200 font-medium truncate" data-testid="suggestion-recipe-name">
                            {top.recipe_name}
                          </span>
                          <span className={cn('inline-flex px-1.5 py-0.5 rounded text-[10px] font-bold border', tierChipClass)} data-testid="suggestion-tier">
                            {tier}
                          </span>
                          <span className="inline-flex px-1.5 py-0.5 rounded text-[10px] font-medium border bg-zinc-500/10 text-zinc-400 border-zinc-500/20" data-testid="suggestion-reason">
                            {top.reason} · {(top.confidence * 100).toFixed(0)}%
                          </span>
                          {isDraftSuggestion && (
                            <span className="inline-flex px-1.5 py-0.5 rounded text-[10px] font-medium border bg-amber-500/10 text-amber-400 border-amber-500/20" data-testid="suggestion-status-draft">
                              DRAFT
                            </span>
                          )}
                          <button
                            onClick={(e) => { e.stopPropagation(); handleApplySuggestion(item, top); }}
                            disabled={mapping}
                            className={cn(
                              'ml-auto inline-flex items-center gap-1 px-2 py-1 rounded text-[11px] font-bold transition-colors disabled:opacity-50',
                              isLowTier
                                ? 'bg-zinc-600/20 hover:bg-zinc-600/30 text-zinc-300 border border-zinc-500/30'
                                : isDraftSuggestion
                                  ? 'bg-amber-500/10 hover:bg-amber-500/20 text-amber-300 border border-amber-500/30'
                                  : 'bg-emerald-500/10 hover:bg-emerald-500/20 text-emerald-300 border border-emerald-500/30'
                            )}
                            data-testid="suggestion-apply-button"
                            title={
                              isLowTier
                                ? 'LOW confidence — opens review modal before mapping'
                                : isDraftSuggestion
                                  ? 'Publish recipe and create mapping in one click'
                                  : 'Create mapping'
                            }
                          >
                            <Zap className="w-3 h-3" />
                            {isLowTier ? 'Review' : isDraftSuggestion ? 'Publish & Map' : 'Apply'}
                          </button>
                        </div>
                      )}
                      {!top && !suggestionsLoading && (
                        <div className="px-4 pb-3 -mt-1">
                          <span className="text-[10px] text-zinc-600 italic">No automatic suggestion — pick a recipe manually.</span>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>

        {/* Published Recipes */}
        <div className="bg-[#18181b] border border-[#27272a] rounded-lg">
          <div className="p-4 border-b border-[#27272a]">
            <div className="flex items-center gap-2 mb-3">
              <BookOpen className="w-5 h-5 text-emerald-500" />
              <h2 className="text-lg font-bold">Published Recipes</h2>
              <span className="text-xs text-zinc-500">(menu items)</span>
            </div>
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-400" />
              <input
                type="text"
                placeholder="Search recipes..."
                value={recipeSearch}
                onChange={(e) => setRecipeSearch(e.target.value)}
                className="w-full pl-10 pr-4 py-2 bg-[#09090b] border border-[#3f3f46] rounded-md focus:border-emerald-500 outline-none text-sm"
                data-testid="recipe-search-input"
              />
            </div>
          </div>
          <div className="max-h-96 overflow-y-auto">
            {filteredRecipes.length === 0 ? (
              <div className="p-8 text-center text-zinc-500">
                <BookOpen className="w-12 h-12 mx-auto mb-3 opacity-50" />
                <p>No published menu recipes</p>
                <button
                  onClick={() => navigate('/recipes/new')}
                  className="mt-2 text-emerald-500 hover:text-emerald-400 text-sm"
                >
                  Create a recipe
                </button>
              </div>
            ) : (
              <div className="divide-y divide-[#27272a]">
                {filteredRecipes.map((recipe) => {
                  const isSelected = selectedRecipe?.recipe_id === recipe.recipe_id;
                  const mappedItemName = getRecipeMappedName(recipe);
                  return (
                    <button
                      key={recipe.recipe_id}
                      onClick={() => setSelectedRecipe(isSelected ? null : recipe)}
                      className={cn(
                        'w-full p-4 text-left transition-colors',
                        isSelected ? 'bg-emerald-500/10' : 'hover:bg-[#09090b]',
                      )}
                      data-testid="recipe-item"
                    >
                      <div className="flex items-center justify-between">
                        <div className="min-w-0 flex-1">
                          <div className="flex items-center gap-2 flex-wrap">
                            <p className="font-medium truncate">{recipe.recipe_name}</p>
                            {(() => {
                              const s = (recipe.status || 'DRAFT').toUpperCase();
                              const cls =
                                s === 'PUBLISHED' ? 'bg-emerald-500/10 text-emerald-400 border-emerald-500/20' :
                                                    'bg-amber-500/10 text-amber-400 border-amber-500/20';
                              return (
                                <span className={cn('inline-flex px-1.5 py-0.5 rounded text-[10px] font-bold border', cls)} data-testid="recipe-status-badge">
                                  {s}
                                </span>
                              );
                            })()}
                            {(() => {
                              const c = recipe.recipe_confidence || 'LOW';
                              const cls =
                                c === 'HIGH'   ? 'bg-emerald-500/10 text-emerald-400 border-emerald-500/20' :
                                c === 'MEDIUM' ? 'bg-blue-500/10 text-blue-400 border-blue-500/20' :
                                                 'bg-amber-500/10 text-amber-400 border-amber-500/20';
                              return (
                                <span className={cn('inline-flex px-1.5 py-0.5 rounded text-[10px] font-medium border', cls)} data-testid="mapping-recipe-confidence">
                                  {c}
                                </span>
                              );
                            })()}
                          </div>
                          {mappedItemName && (
                            <p className="text-xs text-emerald-500 flex items-center gap-1 mt-0.5">
                              <Link2 className="w-3 h-3" />
                              Linked from: {mappedItemName}
                            </p>
                          )}
                        </div>
                        {isSelected && (
                          <div className="w-6 h-6 rounded-full bg-emerald-500 flex items-center justify-center flex-shrink-0">
                            <Check className="w-4 h-4 text-white" />
                          </div>
                        )}
                      </div>
                    </button>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Map action */}
      <div className="flex items-center justify-center">
        <div className="bg-[#18181b] border border-[#27272a] rounded-lg p-6 flex flex-col items-center gap-4">
          <div className="flex items-center gap-4">
            <div className={cn(
              'px-4 py-2 rounded-lg border text-sm max-w-[240px] truncate',
              selectedItem
                ? 'bg-blue-500/10 border-blue-500/20 text-blue-400'
                : 'bg-[#09090b] border-[#27272a] text-zinc-500',
            )}>
              {selectedItem ? selectedItem.item_name : 'Select sales item'}
            </div>
            <Link2 className="w-6 h-6 text-zinc-500" />
            <div className={cn(
              'px-4 py-2 rounded-lg border text-sm max-w-[240px] truncate',
              selectedRecipe
                ? 'bg-emerald-500/10 border-emerald-500/20 text-emerald-400'
                : 'bg-[#09090b] border-[#27272a] text-zinc-500',
            )}>
              {selectedRecipe ? selectedRecipe.recipe_name : 'Select recipe'}
            </div>
          </div>
          <button
            onClick={handleMap}
            disabled={!selectedItem || !selectedRecipe || mapping}
            className={cn(
              "flex items-center gap-2 px-6 py-2 rounded-md transition-colors text-sm font-bold text-white disabled:opacity-50 disabled:cursor-not-allowed",
              selectedRecipe && (selectedRecipe.status || 'DRAFT').toUpperCase() === 'DRAFT'
                ? "bg-amber-500 hover:bg-amber-600"
                : "bg-emerald-500 hover:bg-emerald-600"
            )}
            data-testid="create-mapping-button"
          >
            {selectedRecipe && (selectedRecipe.status || 'DRAFT').toUpperCase() === 'DRAFT'
              ? <Zap className="w-4 h-4" />
              : <Link2 className="w-4 h-4" />}
            {mapping
              ? 'Working...'
              : (selectedRecipe && (selectedRecipe.status || 'DRAFT').toUpperCase() === 'DRAFT'
                  ? 'Publish & Map'
                  : 'Create Mapping')}
          </button>
        </div>
      </div>

      <BulkMappingImportDialog
        open={bulkImportOpen}
        onClose={() => setBulkImportOpen(false)}
        onComplete={() => { setBulkImportOpen(false); loadData(); }}
        outletId={selectedOutlet}
      />
      <MappingConfirmModal
        modal={confirmModal}
        onClose={() => setConfirmModal(null)}
        onConfirmLow={() => {
          const { item, candidate } = confirmModal || {};
          if (item && candidate) handleApplySuggestion(item, candidate, { confirmed: true });
        }}
        onConfirmMultiMap={handleConfirmMultiMap}
      />
    </div>
  );
};

// ── Confirm modals (Trust Polish — May 7 2026) ──────────────────────────────
//   - low_confidence: LOW-tier suggestion review (operator must confirm reasons)
//   - multi_map: recipe already maps to other POS items (intentional_multi_map)
//   - publishability: structural diagnostics — actionable, not "Recipe invalid"

const MappingConfirmModal = ({ modal, onClose, onConfirmLow, onConfirmMultiMap }) => {
  if (!modal) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm p-4"
      data-testid="mapping-confirm-modal"
      role="dialog"
      aria-modal="true"
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div className="bg-[#0a0a0a] border border-[#27272a] rounded-xl max-w-lg w-full max-h-[80vh] overflow-y-auto">
        {modal.kind === 'low_confidence' && (
          <div className="p-6 space-y-4">
            <div className="flex items-center gap-2">
              <AlertCircle className="w-5 h-5 text-zinc-400" />
              <h3 className="text-lg font-semibold">Low confidence suggestion</h3>
            </div>
            <p className="text-sm text-zinc-400">
              The suggestion engine isn't sure about this match. Please review before applying.
            </p>
            <div className="bg-[#18181b] border border-[#27272a] rounded-lg p-4 space-y-2 text-sm">
              <div className="flex items-center justify-between gap-4">
                <span className="text-zinc-400">POS item:</span>
                <span className="font-medium text-zinc-100">{modal.item?.item_name}</span>
              </div>
              <div className="flex items-center justify-between gap-4">
                <span className="text-zinc-400">Suggested recipe:</span>
                <span className="font-medium text-zinc-100">{modal.candidate?.recipe_name}</span>
              </div>
              <div className="flex items-center justify-between gap-4">
                <span className="text-zinc-400">Why this suggestion:</span>
                <span className="font-mono text-xs text-zinc-300">
                  {modal.candidate?.reason} · {((modal.candidate?.confidence || 0) * 100).toFixed(0)}%
                </span>
              </div>
              <div className="flex items-center justify-between gap-4">
                <span className="text-zinc-400">Recipe status:</span>
                <span className="font-mono text-xs text-zinc-300">{modal.candidate?.status}</span>
              </div>
            </div>
            <div className="flex justify-end gap-2">
              <button
                onClick={onClose}
                className="px-4 py-2 bg-[#27272a] hover:bg-[#3f3f46] rounded-md text-sm font-medium"
                data-testid="modal-cancel"
              >
                Cancel
              </button>
              <button
                onClick={onConfirmLow}
                className="px-4 py-2 bg-emerald-500 hover:bg-emerald-600 rounded-md text-sm font-bold text-white"
                data-testid="modal-confirm-low"
              >
                Confirm — apply mapping
              </button>
            </div>
          </div>
        )}

        {modal.kind === 'multi_map' && (
          <div className="p-6 space-y-4" data-testid="modal-multi-map">
            <div className="flex items-center gap-2">
              <AlertCircle className="w-5 h-5 text-amber-400" />
              <h3 className="text-lg font-semibold">Recipe already mapped elsewhere</h3>
            </div>
            <p className="text-sm text-zinc-400">
              This recipe already maps to {modal.other_mappings?.length} other POS item(s).
              Multi-mapping is allowed when intentional (e.g. "Chicken Burger" + "Chicken Burger Combo"
              both deplete the same recipe), but please confirm — silent duplicate depletion paths cause
              inventory errors.
            </p>
            <div className="bg-[#18181b] border border-amber-500/20 rounded-lg p-4 space-y-1.5 text-sm" data-testid="modal-multi-map-list">
              <p className="text-xs text-zinc-500 uppercase tracking-wide mb-1">Already mapped to:</p>
              {(modal.other_mappings || []).map((m, i) => (
                <div key={i} className="flex items-center justify-between gap-3">
                  <span className="text-zinc-200 font-medium truncate">• {m.item_name}</span>
                  {m.intentional_multi_map && (
                    <span className="text-[10px] px-1.5 py-0.5 rounded bg-amber-500/10 text-amber-400 border border-amber-500/20">
                      intentional
                    </span>
                  )}
                </div>
              ))}
            </div>
            <div className="flex justify-end gap-2">
              <button
                onClick={onClose}
                className="px-4 py-2 bg-[#27272a] hover:bg-[#3f3f46] rounded-md text-sm font-medium"
                data-testid="modal-cancel"
              >
                Cancel
              </button>
              <button
                onClick={onConfirmMultiMap}
                className="px-4 py-2 bg-amber-500 hover:bg-amber-600 rounded-md text-sm font-bold text-white"
                data-testid="modal-confirm-multi-map"
              >
                Confirm intentional multi-map
              </button>
            </div>
          </div>
        )}

        {modal.kind === 'publishability' && (
          <div className="p-6 space-y-4" data-testid="modal-publishability">
            <div className="flex items-center gap-2">
              <AlertCircle className="w-5 h-5 text-red-400" />
              <h3 className="text-lg font-semibold">Recipe is not publishable</h3>
            </div>
            <p className="text-sm text-zinc-400">
              Recipe <span className="text-zinc-100 font-medium">"{modal.recipe_name}"</span> has structural issues that
              would corrupt depletion. Fix the issues below, then map.
            </p>
            <div className="bg-[#18181b] border border-red-500/20 rounded-lg p-4 space-y-2 text-sm" data-testid="modal-diagnostics">
              {(modal.diagnostics || []).map((d, i) => (
                <div key={i} className="flex items-start gap-2">
                  <span className="font-mono text-[10px] px-1.5 py-0.5 rounded bg-red-500/10 text-red-300 border border-red-500/20 flex-shrink-0">
                    {d.code}
                  </span>
                  <span className="text-zinc-300">{d.message}</span>
                </div>
              ))}
            </div>
            <div className="flex justify-end">
              <button
                onClick={onClose}
                className="px-4 py-2 bg-[#27272a] hover:bg-[#3f3f46] rounded-md text-sm font-medium"
                data-testid="modal-close"
              >
                Close
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default MenuMapping;
